import { useState } from "react";
import axios from "axios";
import { FormControl, Link, TextField } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ArrowForward, Search } from "@mui/icons-material";
import localize from "../../common/localize";
import ModalBackground from "./ModalBackground";
import TableComponent from "./TableComponent";
import { getInputLabelProps, getInputProps } from "../../common/appStyles";
import ActionButtons from "./ActionButtons";
import { BackForeColor } from "./BackForeColor";
import {
  generateNoDataValues,
  getOperationalStatusNameFromId,
  getOperationalStatusStyleName,
} from "../../common/utils";
import ValueOrNoDataLabel from "./ValueOrNoDataLabel";
import TotalValueItem from "../../pages/event/components/TotalValueItem";
import useGlobalContext from "../../hooks/useGlobalContext";

export default function EventSearch({
  setResultList,
  showSearch,
  setShowSearch,
  singleSearch,
}) {
  const { config, EVENTS_API, getHeadersWithCorrespondingPagination } =
    useGlobalContext();
  const [currentEvents, setCurrentEvents] = useState([]);
  const [codeToSearch, setCodeToSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [errorText, setErrorText] = useState(null);

  const handleCodeChange = (e) => {
    setCodeToSearch(e.target.value);
  };

  const searchByCode = async (code) => {
    try {
      setErrorText(null);
      setIsSearching(true);
      const events = await getEventsInformation(code);

      if (events?.length > 0) {
        const toAdd = [];

        events.forEach((event) => {
          const index = currentEvents.findIndex((x) => x.Id === event.Id);
          if (index < 0) {
            toAdd.push(event);
          }
        });

        if (toAdd.length === 0) {
          setErrorText(localize.error_service_order_already_added);
          setIsSearching(false);
          return;
        }

        setCurrentEvents([...events, ...currentEvents]);
      } else {
        setErrorText(localize.error_no_service_order_found);
      }

      setIsSearching(false);
    } catch {
      setErrorText(localize.error_unexpected);
      setIsSearching(false);
    }
  };

  const getEventsInformation = async (code) => {
    try {
      const events = await axios.get(
        EVENTS_API,
        getHeadersWithCorrespondingPagination(config, 0, [
          {
            Name: "Code",
            FriendlyName: localize.code,
            FilterValue: { Value: code },
            Value: code,
          },
        ]),
      );

      if (events?.data?.Data) {
        setCodeToSearch("");
        return events.data.Data;
      }

      setErrorText(localize.error_no_service_order_found);
      return [];
    } catch {
      setErrorText(localize.error_unexpected);
      return [];
    }
  };

  const removeEventFromList = (eventId) => {
    const index = currentEvents.findIndex((x) => x.Id === eventId);
    if (index >= 0) {
      currentEvents.splice(index, 1);
      setCurrentEvents([...currentEvents]);
    }
  };

  const keyUp = (e) => {
    if (e.key === "Enter") {
      searchByCode(codeToSearch?.trim());
    }
  };

  const getComponents = () => (
    <div className="flex flex-col mt-3" style={{ width: "800px" }}>
      <div
        className="flex"
        style={{
          gap: "10px",
          marginLeft: "16px",
          marginRight: "16px",
          marginBottom: "10px",
        }}
      >
        <FormControl required fullWidth>
          <TextField
            error={errorText?.length > 0}
            helperText={errorText}
            InputLabelProps={getInputLabelProps()}
            inputProps={getInputProps()}
            size="small"
            fullWidth
            id="outlined-basic"
            name="Code"
            label={localize.event_code}
            variant="outlined"
            onChange={handleCodeChange}
            value={codeToSearch}
            onKeyUp={keyUp}
          />
        </FormControl>

        <LoadingButton
          loading={isSearching}
          sx={{
            fontSize: "0.8em",
            backgroundColor: "#10366E",
            width: "150px",
            height: "35px",
          }}
          size="small"
          variant="contained"
          type="button"
          onClick={() => {
            searchByCode(codeToSearch?.trim());
          }}
          disabled={!codeToSearch || codeToSearch?.length === 0}
        >
          <span className="mr-2">{localize.search}</span>
          <Search className="" style={{ marginLeft: "2px" }} />
        </LoadingButton>
      </div>

      <TableComponent
        firstColumnTitle={localize.actions}
        fixedSizeInPx={250}
        greaterLetterSize
        columnTitles={[
          localize.code,
          localize.event_type,
          localize.operational_status,
          localize.execution_status,
          localize.total_value,
          localize.proceeding,
          localize.contractor,
          localize.client_name,
        ]}
        results={
          currentEvents?.length > 0
            ? currentEvents.map((item) => ({
                key: item.Id,
                data: [
                  <ActionButtons
                    hideEditButton
                    key={`${item.Id}_action`}
                    buttons={
                      singleSearch
                        ? [
                            {
                              name: localize.select,
                              icon: (
                                <ArrowForward
                                  style={{
                                    color: "white",
                                    width: "15px",
                                    height: "15px",
                                  }}
                                />
                              ),
                              action: () => {
                                selectItem(item);
                              },
                            },
                          ]
                        : undefined
                    }
                    onclickForDeleteButton={() => {
                      removeEventFromList(item.Id);
                    }}
                  />,
                  <Link
                    color="primary"
                    underline="always"
                    href="#"
                    onClick={() => {
                      window.open(`/events/detail/${item.Id}`, "_blank");
                    }}
                  >{`${item.Code}`}</Link>,
                  <BackForeColor
                    backColor={item?.EventType?.BackgroundColor}
                    foreColor={item?.EventType?.ForegroundColor}
                    text={item?.EventType?.Name}
                  />,
                  <p
                    style={{ borderRadius: "40px" }}
                    className={`rounded p-2 ${getOperationalStatusStyleName(
                      item.OperationalStatus,
                    )}`}
                  >
                    {getOperationalStatusNameFromId(item.OperationalStatus)}
                  </p>,
                  <BackForeColor
                    backColor={
                      item.OperationalStatus === 0
                        ? "white"
                        : item.CurrentStatus?.BackgroundColor
                    }
                    foreColor={
                      item.OperationalStatus === 0
                        ? "red"
                        : item.CurrentStatus?.ForegroundColor
                    }
                    text={
                      item.OperationalStatus === 0
                        ? localize.cancelled
                        : item.CurrentStatus?.Name
                    }
                    styleName={
                      item.OperationalStatus === 0
                        ? "cancelled_operational_status"
                        : ""
                    }
                    TooltipText={
                      item.OperationalStatus === 0
                        ? item.FinishObservation
                        : undefined
                    }
                  />,
                  <TotalValueItem item={item} />,
                  <ValueOrNoDataLabel data={item.ProceedingInfo?.Name} />,
                  <ValueOrNoDataLabel data={item.Contractor?.Name} />,
                  <ValueOrNoDataLabel
                    data={item.ReporterName ?? item.Client?.Name}
                  />,
                ],
              }))
            : [generateNoDataValues(9)]
        }
      />
    </div>
  );

  const selectItem = (item) => {
    setResultList([item]);
    setShowSearch(false);
  };

  const selectCurrentEvents = () => {
    setResultList([...currentEvents]);
    setShowSearch(false);
  };

  return (
    <ModalBackground
      showModal={showSearch}
      Component={getComponents()}
      okButtonText={localize.select}
      hideSubmitButton={singleSearch}
      onClickToSubmit={() => {
        selectCurrentEvents();
      }}
      validators={!currentEvents || currentEvents?.length === 0}
      onClickToCancel={() => {
        setShowSearch(false);
      }}
      cancelButtonText={localize.close}
      title={localize.events_search}
    />
  );
}
